import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid2,
  Box,
  Typography,
  TextField,
  Button,
  Modal,
  InputLabel,
  Tabs,
  Tab,
  Paper
} from "@mui/material";
import { addAssetFile, updateAsset } from "../../actions/mediaAction";
import { useForm } from "react-hook-form";
import ModalBox from "../../themes/overrides/ModalBox";
import OutlinedInput from "../../themes/overrides/OutlinedInput";
import LoadingOverlay from "../../components/LoadingOverlay";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ComputerIcon from '@mui/icons-material/Computer';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import GoogleDriveIcon from '@mui/icons-material/Storage';
import DropboxIcon from '@mui/icons-material/Folder';
import { useParams } from 'react-router-dom';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ marginTop: '20px' }}>
    {value === index && children}
  </div>
);

const AddMedia = ({ isOpen, onClose, onSubmitSuccess, editMode, asset }) => {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();
  const [loading, setLoading] = useState(false);
  const submitError = useSelector((state) => state.assets.errMsg)

  const [selectedTab, setSelectedTab] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    unregister,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      file: null,
    },
  });

  const [error, setError] = useState("");

  useEffect(() => {
    if (editMode && asset) {
      setValue("title", asset.title);
      setValue("file", asset.fileName);
      setValue("description", asset.description);
    } else {
      reset();
    }
  }, [editMode, asset]);


  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleFileSelect = (files) => {
    if (files?.[0]) {
      setSelectedFile(files[0]);
      setValue('file', files);

      if (files[0].type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(files[0]);
      } else {
        setPreview(null);
      }
      unregister('file');
    }
  };


  const handleInputChange = (e) => {
    const file = e.target.files[0];

    const acceptedTypes = ["image/jpg", "image/jpeg", "image/png", "image/webp"];

    if (file && !acceptedTypes.includes(file.type)) {
      setError("Invalid file type. Only .jpg, .png, and .webp are allowed.");
      e.target.value = "";
    } else {
      setError("");
      handleFileSelect(e.target.files);
    }
  };

  const onSubmit = async (data) => {
    if (!data.title) {
      setError("Title is required.");
      return;
    }

    if (!editMode && !selectedFile) {
      setError("File is required.");
      return;
    }

    if (data.title.trim() == "") {
      setError("Title cannot be empty or just spaces");
      return;
    }

    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("description", data.description);
    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    formData.append("workspaceId", workspaceId);

    setLoading(true);
    try {
      if (editMode) {
        const editFormData = new FormData();
        editFormData.append("title", data.title);
        editFormData.append("description", data.description);

        const success = await dispatch(updateAsset(asset._id, editFormData));

        if(submitError) {
          setError(submitError)
        }

        if (success) {
          onSubmitSuccess(success);
          reset();
          onClose();
        }
      } else {
        const success = await dispatch(addAssetFile(formData));
        
        if(submitError) {
          console.log('SUBMIT ERROR : ', submitError)
          setError(submitError)
        }
        if (success?.message === 'Filename already exist') {
          setError('File already exists');
          return;
        }

        if (success) {
          onSubmitSuccess(success);
          setSelectedFile(null);
          setPreview(null);
          reset();
          onClose();
        }
      }
    } catch (error) {
      setError("Upload Failed, Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <ModalBox
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            width: { xs: '95%', sm: '600px', md: '700px' },
            maxHeight: { xs: '95vh', sm: '90vh' },
          }}
        >
          <Typography variant="h2" sx={{ marginBottom: 2 }}>
            {editMode ? "Edit Media" : "Add Media"}
          </Typography>
          <InputLabel shrink htmlFor="title">
            Title
          </InputLabel>
          <OutlinedInput
            fullWidth
            name="title"
            {...register("title", { required: "Title is required" })}
            error={!!errors.title}
            helperText={errors.title?.message}
          />
          <InputLabel shrink htmlFor="description">
            Description
          </InputLabel>
          <OutlinedInput
            fullWidth
            name="description"
            {...register("description")}
            maxRows={4}
          />
          {editMode ? (
            <Typography>{asset.fileName}</Typography>
          ) : (
            <>
              <Box>
                <Typography sx={{ mt: 3, mb: 2 }}>
                  Select input source
                </Typography>

                <Tabs
                  value={selectedTab}
                  onChange={(e, newValue) => setSelectedTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, width: '100%' }}
                >
                  <Tab icon={<ComputerIcon />} label="My Device" />
                  {/* <Tab icon={<InsertLinkIcon />} label="Link" />
                  <Tab icon={<GoogleDriveIcon />} label="Google Drive" />
                  <Tab icon={<DropboxIcon />} label="Dropbox" /> */}
                </Tabs>

                <TabPanel value={selectedTab} index={0}>
                  <Box
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    sx={{
                      border: '2px dashed',
                      borderColor: dragActive ? 'primary.main' : 'grey.300',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      backgroundColor: dragActive ? 'action.hover' : 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept=".jpg, .png, .webp"
                      onChange={handleInputChange}
                      style={{ display: "none" }}
                      id="fileInput"
                    />
                    <label htmlFor="fileInput" style={{ width: '100%', cursor: 'pointer' }}>
                      <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        {selectedFile ? selectedFile.name : 'Drag and drop files here'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {selectedFile ? 'Click to change file' : 'or click to browse'}
                      </Typography>
                    </label>
                  </Box>

                  {selectedFile && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected File: {selectedFile.name}
                      </Typography>
                      {preview && (
                        <Box
                          component="img"
                          src={preview}
                          alt="Preview"
                          sx={{
                            width: '200px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: 1
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {errors.file && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {errors.file.message}
                    </Typography>
                  )}
                </TabPanel>
              </Box>
            </>
          )}
          {error && (
            <Typography variant="body2" color="error" sx={{ marginBottom: 2, mt: 1 }}>
              {error}
            </Typography>
          )}
          <Button type="submit" fullWidth variant="contained" color="primary" sx={{ mt: 2 }}>
            Publish
          </Button>
          {loading && <LoadingOverlay />}
        </ModalBox>
      </Modal>
    </>
  );
};

export default AddMedia;
